import { useRef, useEffect, useImperativeHandle, forwardRef, useState } from 'react';
import { Trash2 } from 'lucide-react';

export interface SignaturePadRef {
  getBase64(): string | null;
  isEmpty(): boolean;
  clear(): void;
}

interface Props {
  width?: number;
  height?: number;
  className?: string;
}

export const SignaturePad = forwardRef<SignaturePadRef, Props>(
  ({ width = 480, height = 180, className }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const drawing   = useRef(false);
    const [empty, setEmpty] = useState(true);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth   = 2;
      ctx.lineCap     = 'round';
      ctx.lineJoin    = 'round';
    }, []);

    function getPos(e: MouseEvent | Touch, canvas: HTMLCanvasElement) {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width  / rect.width;
      const scaleY = canvas.height / rect.height;
      return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
    }

    function startDraw(e: React.MouseEvent | React.TouchEvent) {
      drawing.current = true;
      const canvas = canvasRef.current!;
      const ctx    = canvas.getContext('2d')!;
      const raw    = 'touches' in e ? e.touches[0] : e.nativeEvent as MouseEvent;
      const pos    = getPos(raw as any, canvas);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      setEmpty(false);
    }

    function draw(e: React.MouseEvent | React.TouchEvent) {
      if (!drawing.current) return;
      e.preventDefault();
      const canvas = canvasRef.current!;
      const ctx    = canvas.getContext('2d')!;
      const raw    = 'touches' in e ? e.touches[0] : e.nativeEvent as MouseEvent;
      const pos    = getPos(raw as any, canvas);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    }

    function stopDraw() { drawing.current = false; }

    function clear() {
      const canvas = canvasRef.current!;
      const ctx    = canvas.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      setEmpty(true);
    }

    useImperativeHandle(ref, () => ({
      getBase64() {
        return empty ? null : canvasRef.current?.toDataURL('image/png') ?? null;
      },
      isEmpty() { return empty; },
      clear,
    }));

    return (
      <div className={className}>
        <div className="relative border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-white">
          <canvas
            ref={canvasRef}
            width={width}
            height={height}
            className="w-full touch-none cursor-crosshair"
            style={{ display: 'block' }}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={stopDraw}
            onMouseLeave={stopDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={stopDraw}
          />
          {empty && (
            <p className="absolute inset-0 flex items-center justify-center text-xs text-gray-400 pointer-events-none select-none">
              Firme aquí
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={clear}
          disabled={empty}
          className="mt-2 flex items-center gap-1.5 text-xs text-text-secondary hover:text-red-500 disabled:opacity-30 transition-colors">
          <Trash2 size={12} /> Limpiar firma
        </button>
      </div>
    );
  }
);
SignaturePad.displayName = 'SignaturePad';
